import { Code, ConnectError } from '@bufbuild/connect';
import {
  ContentApprovalStatusEnum,
  ResourceCategoryEnum,
} from '@protos/content_management/content.db_pb';
import { ResourceInfo } from '@protos/content_management/content.common.apis_pb';

// Matches the backend's per-request limit for resource_ids.
export const MAX_LEARNING_HW_RESOURCES = 200;

// Mirrors the backend's student-visibility rule (getStudentUnavailableResourceIds):
// Geneo content, or school content (e.g. admin uploads) that is approved and open
// to this section. Content locks are checked separately. The backend stays the authority.
export const isAssignableResource = (
  resource: ResourceInfo,
  sectionId?: bigint
) => {
  const info = resource.schoolContentInfo;
  if (!info) return true;
  return (
    info.approvalInfo?.approvalStatus ===
      ContentApprovalStatusEnum.CONTENT_APPROVAL_STATUS_APPROVED &&
    (!!info.openForAllSections ||
      (!!sectionId && info.createdInSectionId === sectionId))
  );
};

// School uploads keep their posters in the user data bucket.
export const getResourcePosterBucket = (resource: ResourceInfo) =>
  resource.resourceCategoryType ===
  ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
    ? 'userDataBucket'
    : 'processedMediaBucket';

// Server-authored validation messages that are safe and useful to show as is.
const SERVER_MESSAGES = [
  'Some selected resources are not available to students.',
  'Invalid resourceIds.',
];

export const getLearningHwErrorMessage = (err: unknown, fallback: string) => {
  if (!navigator.onLine) {
    return 'You are offline. Please reconnect and try again.';
  }
  const connectError = ConnectError.from(err);
  const serverMessage = SERVER_MESSAGES.find((message) =>
    connectError.rawMessage.includes(message)
  );
  if (serverMessage) return serverMessage;
  switch (connectError.code) {
    case Code.InvalidArgument:
      return 'Some details are invalid. Please check and try again.';
    case Code.PermissionDenied:
      return 'You do not have access to do this.';
    case Code.NotFound:
      return 'This homework no longer exists.';
    case Code.FailedPrecondition:
      return 'This action is not possible right now.';
    case Code.DeadlineExceeded:
      return 'The request timed out. Please try again.';
    default:
      return fallback;
  }
};
